import {
  type CheckIsValidInvitationInvalidReason,
  InvitationConsumeResult,
} from '@breakoutlearning/firebase-repository/types'
import { PageTitle } from 'components/PageTitle'
import { Spinner } from 'components/Spinner'
import { useRepository } from 'hooks/auth'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useEffect, useId } from 'react'
import { toast } from 'react-hot-toast'
import { InvitationAcceptedDialog } from './InvitationAcceptedDialog'
import { InstructorSectionCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorSectionCubit'
import { Trans } from 'react-i18next'
import { useCubitBuilder } from 'hooks/cubits'
import { InvitationTokenCubit } from '@breakoutlearning/firebase-repository/cubits/InvitationTokenCubit'
import { type FirebaseRepository } from '@breakoutlearning/firebase-repository/models/FirebaseRepository'
import { Dialog } from 'components/dialogs/Dialog'
import { CircleAlert } from 'components/icons/CircleAlert'
import { useTranslationTyped } from 'i18n/i18n'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { when } from 'mobx'
import { BreakoutUserAvatar } from 'components/breakout/BreakoutUserAvatar'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'

export function InvitationTokenPage() {
  const repository = useRepository()
  const rootStore = useRootStore()
  const router = rootStore.router
  const user = useBreakoutUser()
  const { showDialog, popDialog } = useDialogs()
  const { tt } = useTranslationTyped()

  /**
   * as of now all this does is act as a proxy to call the isSectionInvitationFunction
   * but we need it to maintain our pattern of not calling firestore functions directly
   * from view. tldr: we need cubit as the C in MVC
   */
  const cubit = useCubitBuilder(
    () => new InvitationTokenCubit(repository),
    [repository]
  )

  useEffect(() => {
    const token = router.params?.token

    const didVerifyInvitation =
      'invitationVerified' in (router.queryParams ?? {}) &&
      router.queryParams?.invitationVerified === true

    async function acceptInvitation() {
      // we shouldn't ever hit this, but belt and suspenders
      if (user.isAnonymous) return
      if (!token || typeof token !== 'string') {
        rootStore.navigateTo('home')
        return
      }

      // if the user is corre, we need to check if this is a section invitation
      if (user.isCorre) {
        const isSectionInvitation = await cubit.isSectionInvitation(token)
        if (!isSectionInvitation) {
          toast.error(tt.invitation.admin_denied())
          return rootStore.navigateTo('home')
        }
      }

      await when(() => repository.featureFlags.isLoaded, { timeout: 5000 })

      const verificationResult = await verifyInvitationAndPromptAcceptance({
        invitationToken: token,
        repository,
        didVerifyInvitation,
        showDialog,
        popDialog,
      })

      const response = await (async () => {
        if (verificationResult.acceptInvitation)
          return await user.consumeInvitation(token)
        if (
          'handledErrorResult' in verificationResult &&
          verificationResult.handledErrorResult ===
            InvitationConsumeResult.errorAlreadyConsumed
        ) {
          const invitation = await cubit.tryFetchConsumedInvitation(token)
          if (invitation)
            return {
              invitation: invitation.data,
              result: InvitationConsumeResult.errorAlreadyConsumed,
            }
        }
      })()

      const invitationConsumeResult =
        'handledErrorResult' in verificationResult &&
        verificationResult.handledErrorResult
          ? verificationResult.handledErrorResult
          : response?.result

      const slideDeckReviewId =
        response &&
        'invitation' in response &&
        response.invitation &&
        response.invitation.slideDeckIdToReview
          ? response.invitation.slideDeckIdToReview
          : undefined

      switch (invitationConsumeResult) {
        case InvitationConsumeResult.success:
          toast.success(tt.invitation.accepted(), {
            position: 'bottom-center',
          })
          break
        case InvitationConsumeResult.errorAlreadyConsumed:
          if (!slideDeckReviewId) toast.error(tt.invitation.already_accepted())
          break
        case InvitationConsumeResult.errorCreatedByYou:
          toast.error(tt.invitation.created_by_you())
          break
        case InvitationConsumeResult.errorInvitationExpired:
          toast.error(tt.invitation.expired())
          break
        case InvitationConsumeResult.errorInvitationNotFound:
          toast.error(tt.invitation.not_found())
          break
        case InvitationConsumeResult.errorPromotionAlreadyConsumed:
          toast.error(tt.invitation.already_accepted_promotion())
          break
        case InvitationConsumeResult.errorNonLTIUserJoiningLTISection:
          toast.error(tt.invitation.non_lti_user_joining_lti_section())
          break
        case InvitationConsumeResult.errorLTIUserJoiningNonLTISection:
          toast.error(tt.invitation.lti_user_joining_non_lti_section())
          break
        case InvitationConsumeResult.errorAlreadyEnrolled:
          toast.error(tt.invitation.already_enrolled())
          break
        // if no consumption result, we don't need to show a toast
        case undefined:
          break
      }

      // try to navigate to review/{slideDeckId} if review invite
      if (slideDeckReviewId) {
        return rootStore.navigateTo('slideDeckReview', {
          slideDeckId: slideDeckReviewId,
        })
      }

      const sectionIdForConsumedInvitation =
        response && 'invitation' in response && response.invitation
          ? response.invitation?.sectionId
          : undefined

      const navigateTo =
        'navigateTo' in verificationResult && verificationResult.navigateTo
          ? verificationResult.navigateTo
          : 'home'

      rootStore.navigateToWithHook(navigateTo, {
        hook: async () => {
          if (
            invitationConsumeResult !== InvitationConsumeResult.success ||
            !sectionIdForConsumedInvitation
          ) {
            return
          }

          const cubit = new InstructorSectionCubit(
            repository,
            sectionIdForConsumedInvitation
          )

          const section = cubit.section

          // wait for the doc to prevent spinners
          await cubit.section.ready()

          if (section) {
            showDialog(() => <InvitationAcceptedDialog section={section} />)
          }
        },
      })
    }

    void acceptInvitation()
  }, [
    user,
    rootStore,
    router,
    showDialog,
    repository,
    cubit,
    popDialog,
    tt.invitation,
  ])

  return (
    <div className="relative box-border flex h-full items-center justify-center overflow-auto rounded-3xl bg-core-tertiary">
      <PageTitle title={tt.invitation.page_title()} />
      <h1 className="sr-only">{tt.invitation.page_title()}</h1>
      <Spinner />
    </div>
  )
}

const VerifyInvitationDialog = ({
  checkInviteResult,
  logout,
  reject,
  accept,
}: {
  checkInviteResult: Awaited<
    ReturnType<FirebaseRepository['checkIsValidInvitation']>
  >
  logout: () => void
  reject: () => void
  accept: () => void
}) => {
  const { tt } = useTranslationTyped()
  const user = useBreakoutUser()
  const headingId = useId()

  // when the dialog is unmounted, fire reject to cancel the promise
  useEffect(() => () => reject(), [reject])

  return (
    <Dialog
      size="xl"
      testId="assign-class-dialog"
      className={'flex h-[700px] w-[1000px] items-center justify-center'}
      innerClassName="flex flex-col h-full items-center justify-center gap-6 !min-h-0 w-[450px] !px-0"
      aria-labelledby={headingId}
    >
      <PageTitle title={tt.invitation.page_title()} />
      <DialogCloseButton />
      {!checkInviteResult.isValid && (
        <>
          <div className="flex flex-col items-center gap-3">
            {checkInviteResult &&
              'invitationType' in checkInviteResult &&
              checkInviteResult.invitationType === 'section' && (
                <div className="flex max-w-min flex-row items-center gap-1 rounded-full bg-core-secondary px-4 py-1">
                  <CircleAlert className="stroke-core-error" size={12.5} />
                  <p className="text-nowrap text-label-medium text-core-on-secondary">
                    {tt.invitation.verify_invitation.student_invitation()}
                  </p>
                </div>
              )}
            <h1 id={headingId} className="text-headline-large">
              {tt.invitation.verify_invitation.invalid_invitation()}
            </h1>
            <p className="text-body-large">
              {tt.invitation.verify_invitation.invalid_invitation_sub()}
            </p>
          </div>
          <BreakoutButton kind="accent" onClick={reject}>
            {tt.invitation.verify_invitation.back_to_home()}
          </BreakoutButton>
        </>
      )}
      {checkInviteResult.isValid && (
        <>
          <div className="flex w-full flex-col items-center gap-3">
            <div className="flex max-w-min flex-row items-center gap-1 rounded-full bg-core-secondary px-4 py-1">
              <div className="aspect-square h-2 min-h-2 w-2 min-w-2 rounded-full bg-fixed-orange" />
              <p className="text-nowrap text-label-medium text-core-on-secondary">
                {tt.invitation.verify_invitation.student_invitation()}
              </p>
            </div>
            <h1 id={headingId} className="text-headline-large">
              {tt.invitation.verify_invitation.new_class_invitation()}
            </h1>
            <p className="text-body-large">
              {tt.invitation.verify_invitation.new_class_invitation_sub()}
            </p>

            <div className="flex flex-row items-center gap-1 rounded-xl border bg-fixed-eggshell stroke-fixed-rust p-2 pr-4 text-body-large">
              <div className="p-2">
                <CircleAlert className="stroke-fixed-rust" size={24} />
              </div>
              <div>
                <p>{tt.login.verify_invitation.verify_invitation_warning()}</p>
              </div>
            </div>
          </div>
          {checkInviteResult.invitationType === 'section' && (
            <dl className="flex w-full flex-col gap-3 px-8">
              <LabeledSectionDetail
                label={tt.invitation.verify_invitation.class_name()}
              >
                {checkInviteResult.section.data.className}
              </LabeledSectionDetail>
              <LabeledSectionDetail
                label={tt.invitation.verify_invitation.section_name()}
              >
                {checkInviteResult.section.data.sectionName}
              </LabeledSectionDetail>
              <LabeledSectionDetail
                label={tt.invitation.verify_invitation.instructor()}
              >
                <div className="flex flex-row items-center gap-1">
                  <BreakoutUserAvatar
                    user={checkInviteResult.instructor}
                    radius={10}
                  />
                  <span>{checkInviteResult.instructor.fullName}</span>
                </div>
              </LabeledSectionDetail>
            </dl>
          )}
          <dl className="flex w-full flex-col gap-3 px-8">
            <LabeledSectionDetail
              label={tt.invitation.verify_invitation.your_name()}
            >
              {user.publicUser.fullName}
            </LabeledSectionDetail>
            <LabeledSectionDetail
              label={tt.invitation.verify_invitation.your_email()}
            >
              {user.profile.data.emailAddress}
            </LabeledSectionDetail>
          </dl>
          <div className="px-8 text-center text-body-large">
            <Trans
              i18nKey="invitation.verify_invitation.institutional_email_warning"
              components={{
                button: (
                  <button className="font-bold underline" onClick={logout} />
                ),
                br: <br />,
              }}
            />
          </div>
          <div className="flex w-full flex-row justify-between px-8">
            <BreakoutButton kind="secondary" onClick={reject}>
              {tt.invitation.verify_invitation.reject()}
            </BreakoutButton>
            <AcceptButton
              checkInviteResult={checkInviteResult}
              accept={accept}
            />
          </div>
        </>
      )}
    </Dialog>
  )
}

const LabeledSectionDetail = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => {
  return (
    <div className="text-body-xlarge flex w-full flex-row justify-between gap-2">
      <dt className="text-nowrap">{label}</dt>
      <dd className="truncate text-nowrap font-bold">{children}</dd>
    </div>
  )
}

const AcceptButton = ({
  checkInviteResult,
  accept,
}: {
  checkInviteResult: Awaited<
    ReturnType<FirebaseRepository['checkIsValidInvitation']>
  >
  accept: () => void
}) => {
  const { tt } = useTranslationTyped()

  const sectionStartDate =
    checkInviteResult.isValid &&
    checkInviteResult.invitationType === 'section' &&
    checkInviteResult.section.data.sectionStartDate

  const isSectionNotStarted = sectionStartDate && sectionStartDate > new Date()

  const button = (
    <BreakoutButton
      data-testid="accept-invitation"
      kind="accent"
      onClick={accept}
      disabled={isSectionNotStarted}
    >
      {tt.invitation.verify_invitation.accept()}
    </BreakoutButton>
  )

  if (isSectionNotStarted) {
    return (
      <BreakoutTooltip
        content={tt.invitation.verify_invitation.section_not_accessible_yet()}
      >
        {button}
      </BreakoutTooltip>
    )
  }

  return button
}

async function verifyInvitationAndPromptAcceptance({
  invitationToken,
  repository,
  didVerifyInvitation,
  showDialog,
  popDialog,
}: {
  invitationToken: string
  repository: FirebaseRepository
  didVerifyInvitation: boolean
  showDialog: ReturnType<typeof useDialogs>['showDialog']
  popDialog: ReturnType<typeof useDialogs>['popDialog']
}) {
  if (didVerifyInvitation || !repository.featureFlags.data.verifyInvitation)
    return { acceptInvitation: true }

  const invitationResult =
    await repository.checkIsValidInvitation(invitationToken)

  return await new Promise<
    | { acceptInvitation: true }
    | {
        acceptInvitation: false
        handledErrorResult?: InvitationConsumeResult
        navigateTo?: string
      }
  >((res) => {
    // auto accept invite if it's a valid non-section invitation
    if (
      invitationResult.isValid &&
      invitationResult.invitationType !== 'section'
    )
      return res({ acceptInvitation: true })

    // if we have have a handled invitation error w/ toast, skip the dialog
    if (!invitationResult.isValid) {
      const handledErrorResult = handledErrorResultFromInvalidReason(
        invitationResult.invalidReason
      )

      if (handledErrorResult)
        return res({ acceptInvitation: false, handledErrorResult })
    }

    // prompt user to accept invitation / show invalid if unhandled error
    showDialog(() => (
      <VerifyInvitationDialog
        checkInviteResult={invitationResult}
        accept={() => {
          popDialog()
          res({ acceptInvitation: true })
        }}
        reject={() => {
          popDialog()
          res({ acceptInvitation: false })
        }}
        logout={() => {
          popDialog()
          res({ acceptInvitation: false, navigateTo: 'logout' })
        }}
      />
    ))
  })

  // if (!dialogResult.acceptInvitation)
  //   return rootStore.navigateTo(dialogResult.navigateTo ?? 'home')
}

function handledErrorResultFromInvalidReason(
  invalidReason: CheckIsValidInvitationInvalidReason
): InvitationConsumeResult | null {
  switch (invalidReason) {
    case 'invitationDoesNotExist':
      return InvitationConsumeResult.errorInvitationNotFound
    case 'invitationConsumedByCurrentUser':
      return InvitationConsumeResult.errorAlreadyConsumed
    case 'invitationCreatedByCurrentUser':
      return InvitationConsumeResult.errorCreatedByYou
    case 'invitationExpired':
      return InvitationConsumeResult.errorInvitationExpired
    case 'nonLTIUserAcceptingLTISectionInvitation':
      return InvitationConsumeResult.errorNonLTIUserJoiningLTISection
    case 'ltiUserAcceptingNonLTISectionInvitation':
      return InvitationConsumeResult.errorLTIUserJoiningNonLTISection
    case 'userStudentInSection':
      return InvitationConsumeResult.errorAlreadyEnrolled
    case 'userCollaboratorOnSection':
    case 'userSectionInstructor':
    case 'oneTimeInvitationAlreadyConsumed':
    case 'sectionDoesNotExist':
    case 'userRemovedFromSection':
    case 'internal':
      return null
  }
}
